<template>
  <div
    class="fts-detail-view"
    :data-detail-view-load="loading ? 'loading' : 'loaded'"
  >
    <div class="dv-header" v-if="initial">
      <div class="dv-header-action-container" v-if="enabledControl">
        <!-- activity -->
        <fts-btn
          v-for="activity in headerActivities"
          :key="activity.id"
          outline
          :color="activity.color ? activity.color : 'primary'"
          :emoji="activity.emoji"
          text-transform-init
          class="btn-activity"
          @click="executeActivity(
            activity,
            listData.filter((x) => x.selected)
          )"
        >
          <span class="btn-activity-label">{{ activity.name }}</span>
        </fts-btn>
        <!-- custom -->
        <fts-btn
          v-for="action in headerActions"
          :key="action.id"
          outline
          :color="action.color ? action.color : 'ternary-blue'"
          :emoji="action.emoji"
          text-transform-init
          class="btn-activity"
          @click="executeAction(
            action,
            listData.filter((x) => x.selected)
          )"
        >
          <span>
            {{action.name}}
          </span>
        </fts-btn>
        <!-- document -->
        <fts-btn
          v-for="document in headerDocuments"
          :key="document.id"
          outline
          color="ternary-green"
          text-transform-init
          class="btn-activity"
          @click="executeDocument(
            document,
            listData.filter((x) => x.selected)
          )"
        >
          <font-awesome-icon
            :icon="['far', 'file-pdf']"
            class="fts-file-pdf"
            v-if="document.typeId === 'pdf'"
          />
          <font-awesome-icon
            :icon="['far', 'file-alt']"
            class="fts-file-excel"
            v-else-if="document.typeId === 'spreadsheet'"
          />
          <font-awesome-icon
            :icon="['far', 'file-code']"
            class="fts-file-html"
            v-else-if="document.typeId === 'html'"
          />
          <font-awesome-icon
            :icon="['far', 'file']"
            class="fts-file-default"
            v-else
          />
          <span class="btn-activity-label">{{ document.name }}</span>
        </fts-btn>
        <!-- standard -->
        <fts-btn
          v-for="activity in headerStandards"
          :key="activity.id"
          outline
          text-transform-init
          :color="activity.id === 'delete' ? 'danger' : 'primary'"
          class="btn-activity"
          @click="executeActivity(
            activity,
            listData.filter((x) => x.selected)
          )"
        >
          <i :class="activity.icon"></i>
          <span>{{ activity.name }}</span>
        </fts-btn>
      </div>
      <div class="dv-header-control-container" v-if="enabledControl">
        <div class="dv-header-control-wrapper fts-flex-wrap-reverse">
          <fts-query-designer
            :modules="modules"
            :states="states"
            :information-source="filterColumns"
            :filter="filters"
            mode="view"
            v-if="isCapableOf(SYSTEM_CAPABILITY.Query)"
          />

          <div class="dv-header-control__r">
            <fts-sort
              :modules="modules"
              :fields="columns"
              v-model="sort"
              class="dv-header-control-btn-wrapper"
              :total-items-exceed="isTotalItemsExceed"
              @closed="refreshList"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Sort)"
            />
            <!-- filter settings -->
            <fts-tippy
              content="Filter settings"
              class="dv-header-control-action"
              v-if="isInternal && listingId && listingId !== 'everything'"
            >
              <div style="display: flex;">
                <div style="width: 20px;height: 19px;display: flex;align-items: center;justify-content: center;" v-if="saveLoading">
                  <fts-spinner :size="16" />
                </div>

                <fts-dropdown-select
                  append-to-body
                  prevent-overflow-alt-axis
                  prevent-overflow-tether-off
                  overlay
                  v-else
                >
                  <template #toggle>
                    <font-awesome-icon :icon="['fas', 'filter']" fixed-width />
                  </template>

                  <template #menu="{ select }">
                    <div class="dropdown-option dropdown-option--group" @click="select();saveDefaultFilters()">
                      <div class="dropdown-option-label">Save current filter</div>
                      <div class="dropdown-option-description">
                        Save current selected filters as listing default filters.
                      </div>
                    </div>

                    <div class="dropdown-option dropdown-option--group" @click="select();openListingFilters()">
                      <div clas="dropdown-option-label">Open filter conditions</div>
                      <div class="dropdown-option-description">
                        Open listing filter conditions.
                      </div>
                    </div>
                  </template>
                </fts-dropdown-select>

                <fts-release-badge alpha class="fts-align-top" />
              </div>
            </fts-tippy>
            <!-- import -->
            <fts-tippy
              content="Import data"
              class="dv-header-control-action"
              v-if="isCapableOf(SYSTEM_CAPABILITY.Write) && moduleImport"
            >
              <font-awesome-icon
                :icon="['fas', 'file-upload']"
                fixed-width
                @click="$refs.ftsImport.open()"
              />
            </fts-tippy>
            <!-- export -->
            <fts-tippy
              content="Export data"
              class="dv-header-control-action"
              v-if="isExportRight"
            >
              <font-awesome-icon
                :icon="['fas', 'file-download']"
                fixed-width
                @click="exportData"
              />
              <!-- temp for everything listing -->
              <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
            </fts-tippy>
            <!-- dashboard widget -->
            <fts-tippy
              content="Analytics"
              class="dv-header-control-action"
              v-if="isDashboardWidgetRight"
            >
              <font-awesome-icon
                :icon="['fas', 'chart-column']"
                fixed-width
                @click="openWidgetDesignPreviewModal"
              />
              <!-- temp for everything listing -->
              <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
            </fts-tippy>
            <!-- bulk -->
            <fts-tippy
              :content="`${enabledBulk ? 'Turn off' : 'Turn on'} bulk actions`"
              class="dv-header-control-action"
              :class="{'fts-text-primary': enabledBulk}"
            >
              <font-awesome-icon
                :icon="['fas', 'tasks']"
                fixed-width
                @click="toggleBulk"
              />
            </fts-tippy>
          </div>
        </div>

        <fts-listing-filter-design 
          @refresh="refresh(true)"
          ref="ftsListingFilterDesign" 
        />
      </div>
    </div>

    <div class="dv-content">
      <div class="dv-list">
        <template v-if="initial">
          <div class="dv-list__header">
            <div class="dv-list-header-wrapper">
              <div>
                <fts-checkbox :size="16" v-model="bulkSelectAll" :disabled="!(listData && listData.length)" v-if="enabledBulk" />
                <fts-tippy :content="`${enabledControl ? 'Hide' : 'Show'} control`">
                  <font-awesome-icon
                    :icon="['fas', 'bars']"
                    fixed-width
                    class="dv-list-header-action"
                    :class="{'dv-list-header-action--active': enabledControl}"
                    @click="toggleControl"
                  />
                </fts-tippy>
                <fts-list-designer v-if="isInternal">
                    <font-awesome-icon
                    :icon="['fas', 'wand-magic-sparkles']"
                    fixed-width
                    :color="`var(--sidenav-apex)`"
                    style="cursor: pointer"
                  />

                    <fts-release-badge alpha class="fts-align-top" />
                </fts-list-designer>
              </div>
              <div>
                <fts-tippy content="create new entry" v-if="isCreateRight">
                  <fts-btn
                    outline
                    color="primary"
                    text-transform-init
                    box-shadow-none
                    class="dv-list-header-btn"
                    @click="executeActivity({ id: 'create' }, [])"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />New
                  </fts-btn>
                </fts-tippy>
              </div>
            </div>
          </div>

          <div class="dv-list__content fts-scrollbar" ref="dvListContent">
            <div class="dv-list-message" v-if="listError">
              <div class="dv-list-message__title fts-text-danger">Something went wrong</div>
              <div class="dv-list-message__description">{{listError}}</div>
            </div>

            <div class="dv-list-message" v-else-if="!(listData && listData.length)">
              No results.
            </div>

            <template v-else-if="isInternal && appList">
              <div
                v-for="item in listData"
                :key="item.id"
                class="dv-list-entry"
                :class="{'dv-list-entry--active': item.id == selectedEntryId}"
                @click="entryAction(item)"
              >
                <div class="dv-list-entry__l stopPropagation" v-if="enabledBulk">
                  <fts-checkbox :size="16" v-model="item.selected" />
                </div>
                <fts-designer-locus-card :item="item" :appList="appList" :columns="columns" :states="states" :vectorId="moduleId" :activities="header.activities"  @activity:selected="(activity)=> executeActivity(activity,  [item])" />
              </div>
            </template>

            <template v-else>
              <div
                v-for="item in listData"
                :key="item.id"
                class="dv-list-entry"
                :class="{'dv-list-entry--active': item.id == selectedEntryId}"
                @click="$event => viewForm($event, item)"
              >
                <div class="dv-list-entry__l stopPropagation" v-if="enabledBulk">
                  <fts-checkbox :size="16" v-model="item.selected" />
                </div>
                <div class="dv-list-entry__r">
                  <div class="dv-list-entry-main">
                    <div class="dv-list-entry-title">
                      {{item.title}}
                    </div>

                    <div class="dv-list-entry-comment" v-if="item.lastComment && item.lastComment.id">
                      <fts-tippy
                        :options="{ theme: 'light-border' }"
                        :request="{
                          dispatchName: 'loadHistory',
                          payload: {
                            id: item.lastComment.id,
                            type: item.lastComment.type ?
                              item.lastComment.type :
                              'comment'
                          },
                        }"
                        append-content-class="fts-collaboration-content"
                      >
                        <div
                          slot="trigger"
                          class="dv-list-entry-comment-trigger"
                          :class="{'dv-list-entry-comment-trigger--active': (item.collaborationFlags & 4) === 4}"
                        >
                          <font-awesome-icon :icon="['far', 'comment-alt']" />
                        </div>
                        <template #content="{ content, loading }">
                          <template v-if="content">
                            <div class="collaboration-comment-audit">
                              <fts-profile :userName="content.createdBy" />
                              <div class="comment-from-now">
                                {{ momentRelativeTime(content.createdDate) }}
                              </div>
                            </div>
                            <div
                              class="collaboration-comment-content"
                              v-html="translateCommentContent(content.detail)"
                            ></div>
                            <div class="collaboration-date">
                              {{ moment(content.createdDate).format("llll") }}
                            </div>
                          </template>
                          <fts-spinner :size="16" show v-else-if="loading" />
                        </template>
                      </fts-tippy>
                      <div class="dv-list-entry-comment-count" v-if="item.commentCount">
                        {{ item.commentCount > 99 ? 99 : item.commentCount }}
                      </div>
                    </div>

                    <fts-assign-to
                      :module-id="moduleId"
                      :form-data="item"
                      :max-length="3"
                      avatar-mode
                      :toggle-due="false"
                      :toggle-create="false"
                      :view-mode="!isAssignRight"
                      :comment-section="isCommentRight"
                      class="dv-list-entry-assign stopPropagation"
                      @refresh="refreshList"
                      v-if="item.assignees && item.assignees.length"
                    />

                    <fts-tippy
                      :content="item.state && item.state.name"
                      class="dv-list-state-wrapper"
                      v-if="item.state && item.state.id"
                    >
                      <fts-state
                        :state="item.state"
                        :states="states"
                        class="dv-list-state"
                      />
                    </fts-tippy>
                  </div>

                  <div class="dv-list-entry-sub">
                    <div class="dv-list-entry-document-id" v-if="isColumnOf('documentId')">
                      {{item.documentId}}
                    </div>

                    <div class="dv-list-entry-profile" v-if="item.audit && item.audit.createdBy">
                      <fts-profile
                        :user-name="item.audit.createdBy"
                        :profile-photo="false"
                        profile-name-class="dv-list-entry-profile-name"
                      />
                      <fts-tippy
                        :content="moment(item.audit.createdDate).format('llll')"
                        class="dv-list-entry-profile-date"
                      >
                        <div slot="trigger">
                          {{ momentRelativeTime(item.audit.createdDate) }}
                        </div>
                      </fts-tippy>
                    </div>

                    <fts-tippy
                      :content="`due on ${moment(item.due).format('ll')}`"
                      class="dv-list-entry-assign-date"
                      :class="{'dv-list-entry-assign-date--due': moment(item.due).isBefore(moment().startOf('day'))}"
                      v-if="item.due"
                    >
                      <div>
                        {{calendarRelativeTime(item.due)}}
                      </div>
                    </fts-tippy>
                  </div>
                </div>
              </div>
            </template>

            <infinite-loading
              class="dv-list-infinite-loading"
              @infinite="infiniteHandler"
              v-if="infiniteScroll"
            >
              <fts-spinner slot="spinner" show />
              <div slot="no-more" class="dv-list-infinite-loading-message">
                No more results
              </div>
              <div slot="no-results" class="dv-list-infinite-loading-message">
                No results
              </div>
            </infinite-loading>
          </div>

          <div class="dv-list__footer">
            <div class="dv-list-pagination">
              {{listData.length}} of {{isTotalItemsExceed ? `${getTotalItemsLimit - 1}+` : totalItems}}
            </div>
            <fts-tippy
              content="Refresh list"
              class="dv-list-footer-action"
              :class="{'dv-header-control-action--loading': loading}"
            >
              <font-awesome-icon
                :icon="['fas', 'sync-alt']"
                :spin="loading"
                fixed-width
                @click="!loading && refreshList()"
              />
            </fts-tippy>
          </div>
        </template>

        <div class="dv-list-loading-overlay" v-if="!initial || loading">
          <fts-spinner show />
        </div>
      </div>

      <fts-detail-view-form
        :list-data="listData"
        :design-mode="designMode"
        @refresh-list="refreshList"
        @activity:selected="setFormSelectedActivity"
        ref="detailViewForm"
        v-if="initial"
      />
    </div>

    <fts-action-page-modal ref="ftsActionPageModal" />
    <fts-document-modal ref="documentModal" />
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <fts-import
      :module-id="moduleId"
      :module-import="moduleImport"
      @importDone="refreshList"
      ref="ftsImport"
      v-if="moduleImport"
    />
    <fts-widget-design-preview-modal
      :modules="modules"
      :states="states"
      :users="users"
      ref="ftsWidgetDesignPreviewModal"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import InfiniteLoading from 'vue-infinite-loading';

import commons from '../../commons/commons';
import canvasDesign from '../../commons/canvasDesign';
import queryLogic from '../../commons/queryLogic';
import listing from '../../commons/listing';
import { isCapableOf, Listing_Max_Total_Entries, SYSTEM_CAPABILITY } from '../../commons/flags';
import { authorizationStore } from '../../store/authorizationStore';
import { clientStore } from '../../store/clientStore';
import { store as canvasDesignStore } from '../../store/canvasDesignStore'; 
import LocalStorageService from '../../store/LocalStorageService';

import FTSActionPageModal from '../FTSActionPageModal';
import FTSAssignTo from '../FTSAssignTo';
import FTSBtn from '../FTSBtn';
import FTSCheckbox from '../FTSCheckbox';
import FTSDetailViewForm from './FTSDetailViewForm';
import FTSDocumentModal from '../FTSDocumentModal';
import FTSDocumentFormModal from '../FTSDocumentFormModal';
import FTSImport from '../FTSImport';
import FTSProfile from '../FTSProfile';
import FTSQueryDesigner from '../../design/FTSQueryDesigner';
import FTSReleaseBadge from '../FTSReleaseBadge';
import FTSWidgetDesignPreviewModal from '../../dashboard/design/FTSWidgetDesignPreviewModal';
import FTSWorkflowDiagram from '../FTSWorkflowDiagram';
import FTSSort from '../FTSSort';
import FTSSpinner from '../FTSSpinner';
import FTSState from '../FTSState';
import FTSTippy from '../FTSTippy';
import FTSListDesigner from '../FTSListDesigner';
import FTSDesignerLocusCard from '../FTSDesignerLocusCard';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSListingFilterDesign from '../../design/FTSListingFilterDesign';


const BULK_MAX_COUNT = 50;
const BULK_EXCEED_ERROR = `Selected items exceeded. Maximum only allows to select up to ${BULK_MAX_COUNT}.`;
const getListError = (err) => {
  if (err.response && err.response.data && err.response.data.message) {
    return err.response.data.message;
  }
  return "Failed to load the list, please try again.";
}

export default {
  components: {
    InfiniteLoading,
    'fts-action-page-modal': FTSActionPageModal,
    'fts-assign-to': FTSAssignTo,
    'fts-btn': FTSBtn,
    'fts-checkbox': FTSCheckbox,
    'fts-detail-view-form': FTSDetailViewForm,
    'fts-document-modal': FTSDocumentModal,
    'fts-document-form-modal': FTSDocumentFormModal,
    'fts-import': FTSImport,
    'fts-profile': FTSProfile,
    'fts-query-designer': FTSQueryDesigner,
    'fts-release-badge': FTSReleaseBadge,
    'fts-widget-design-preview-modal': FTSWidgetDesignPreviewModal,
    'fts-workflow-diagram': FTSWorkflowDiagram,
    'fts-sort': FTSSort,
    'fts-spinner': FTSSpinner,
    'fts-state': FTSState,
    'fts-tippy': FTSTippy,
    'fts-list-designer': FTSListDesigner,
    'fts-designer-locus-card': FTSDesignerLocusCard,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-listing-filter-design': FTSListingFilterDesign
  },
  props: {
    modules: Array,
    states: Array,
    users: Array,
    moduleId: [String, Number],
    listingId: String,
    designMode: Boolean
  },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    initial: false,
    initialLoad: false,
    infiniteScroll: false,
    schemaLoad: false,
    header: {},
    columns: [],
    appList: [],
    filters: null,
    originalDefaultFilters: null,
    sort: null,
    moduleImport: null,
    enabledControl: false,
    enabledBulk: false,
    listData: [],
    listError: null,
    currentPage: 0,
    pageSize: 20,
    totalItems: 0,
    totalItemsLimit: 0,
    loading: false,
    formSelectedActivity: null,
    saveLoading: false
  }),
  methods: {
    moment,
    isCapableOf(type) {
      return isCapableOf(type, this.modules, this.moduleId);
    },
    isRightOf(type) {
      return type &&
      this.header &&
      this.header.standards &&
      this.header.standards.indexOf(type) > -1;
    },
    isColumnOf(columnId) {
      return Array.isArray(this.columns) && this.columns.some((x) => x.id === columnId);
    },
    calendarRelativeTime(date) {
      return commons.calendarRelativeTime(date);
    },
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    async refresh(withSchema = false) {
      try {
        const res = await clientStore.dispatch("loadWorkspaceList", {
          moduleId: this.moduleId,
          listingId: this.listingId,
          withHeader: withSchema,
          currentPage: 0,
          type: 'detail',
          pageSize: this.pageSize,
          filters: this.filters,
          sorts: this.sort ? this.sort : null,
          contextId: clientStore.state.workspaceContext != null ?
            clientStore.state.workspaceContext.id :
            null
        });

        if (this.infiniteScroll) {
          this.infiniteScroll = false;
          this.$refs.dvListContent && this.$refs.dvListContent.scrollTo({ top: 0 });
        }

        if (withSchema) {
          this.header = res.data.header;
          this.columns = res.data.header.columns;
          this.appList = res.data.header.appList;
          this.moduleImport = res.data.import;
          // default filters
          if (
            this.header.defaultFilters &&
            this.header.defaultFilters.items &&
            this.header.defaultFilters.items.length
          ) {
            const { filters, originalDefaultFilters } = listing.setDefaultFilters(
              this.header.defaultFilters,
              this.originalDefaultFilters,
              this.filters,
              this.columns
            );

            this.filters = filters;
            this.originalDefaultFilters = originalDefaultFilters;
          } else if (!this.schemaLoad) {
            this.filters = { items: [
              queryLogic.getDefaultQueryFilter(this.columns, { workspaceBeta: clientStore.getters.workspaceBeta })
            ]};
            this.originalDefaultFilters = null;
          }
          // default sort
          if (this.header.defaultSort) {
            this.sort = _.cloneDeep(this.header.defaultSort);
          }
          // system capability
          if (Array.isArray(this.header.standards)) {
            if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
              this.header.standards = this.header.standards.filter((x) =>
                ['create', 'edit', 'changeStatus', 'duplicate', 'assign'].indexOf(x) === -1
              );
            }
            if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
              this.header.standards = this.header.standards.filter((x) => x !== 'delete');
            }
            if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
              this.header.standards = this.header.standards.filter((x) => ['view', 'quickView'].indexOf(x) === -1);
            }
          }

          this.schemaLoad = true;
        }

        this.totalItems = res.data.data.totalItems;
        this.totalItemsLimit = res.data.data.limit;
        this.currentPage = 1;
        this.listData = res.data.data.list;
        this.listError = null;
        this.initialInfiniteScroll();
      } catch(err) {
        console.error(err);
        this.listError = getListError(err);
      }
    },
    async refreshList(item) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return;
      }

      this.loading = true;
      await this.refresh();

      if (
        !this.listError &&
        this.listData[0] &&
        this.$refs.detailViewForm &&
        this.$refs.detailViewForm.header &&
        !this.$refs.detailViewForm.header.id
      ) {
        this.loadForm(
          {
            id: this.listData[0].id,
            title: this.listData[0].title,
            state: this.listData[0].state,
            audit: this.listData[0].audit
          },
          item && item.previousEntry
        );
      }

      this.loading = false;
    },
    initialInfiniteScroll() {
      if (this.totalItems > (this.listData && this.listData.length)) {
        setTimeout(() => {
          this.infiniteScroll = true;
        }, 1000);
      }
    },
    async infiniteHandler($state) {
      let loadMore = false;
      try {
        const res = await clientStore.dispatch('loadWorkspaceList', {
          moduleId: this.moduleId,
          listingId: this.listingId,
          currentPage: this.currentPage,
          pageSize: this.pageSize,
          filters: this.filters,
          sorts: this.sort ? this.sort : null,
          contextId: clientStore.state.workspaceContext != null ?
            clientStore.state.workspaceContext.id :
            null
        });

        this.totalItems = res.data.data.totalItems;
        this.totalItemsLimit = res.data.data.limit;

        if (res.data.data.list && res.data.data.list.length) {
          this.listData = [
            ...this.listData,
            ...res.data.data.list.filter((x) => !this.listData.some((y) => y.id === x.id))
          ];
          if (this.listData && this.listData.length < this.getTotalItemsLimit) {
            loadMore = true;
          }
        }
      } catch(err) {
        console.error(err);
      }

      if (loadMore) {
        this.currentPage += 1;
        $state.loaded();
      } else {
        $state.complete();
      }
    },
    loadDetailViewForm(payload) {
      let formPayload = {...payload};

      if (!formPayload.activityId || ["view", "quickView"].includes(formPayload.activityId)) {
        const isView = formPayload.activityId === "view";
        const isQuickView = formPayload.activityId === "quickView";
        const isFullView = formPayload.fullView === true;

        if ((isView || isFullView) && !this.isViewRight) {
          formPayload.activityId = "quickView";
        } else if ((isQuickView || !isFullView) && !this.isQuickViewRight) {
          formPayload.activityId = "view";
        }

        formPayload.fullView = formPayload.activityId === "view";
      }

      window.eventBus.$emit('loadDetailViewForm', formPayload);
    },
    loadForm(entry, previousEntry) {
      const entryId = entry && entry.id;
      const previousEntryId = previousEntry && previousEntry.id;
      let formPayload = {
        ...entry,
        moduleId: this.moduleId,
        listingId: this.listingId,
        fullView: this.$route.query.m && this.$route.query.m.toLowerCase() === 'f'
      }

      if (
        !this.enabledBulk &&
        entryId &&
        this.header &&
        this.formSelectedActivity &&
        this.formSelectedActivity.id &&
        !["create"].includes(this.formSelectedActivity.id) &&
        (
          !previousEntryId || (entryId != previousEntryId)
        )
      ) {
        const formSelectedActivityId = this.formSelectedActivity.id;

        if (this.formSelectedActivity.standard) {
          if (
            Array.isArray(this.header.standards) &&
            this.header.standards.includes(formSelectedActivityId)
          ) {
            formPayload.activityId = formSelectedActivityId;
          }
        } else if (Array.isArray(this.header.activities)) {
          const findActivity = this.header.activities.find(activity => activity.id === formSelectedActivityId);

          if (findActivity) {
            if (Array.isArray(findActivity.fromStates)) {
              if (entry.state && entry.state.id && findActivity.fromStates.includes(entry.state.id)) {
                formPayload.activityId = formSelectedActivityId;
              }
            } else {
              formPayload.activityId = formSelectedActivityId;
            }
          }
        }
      }

      this.loadDetailViewForm(formPayload);
    },
    viewForm(event, entry) {
      if (commons.elementStopPropagation(event)) {
        return;
      }

      this.loadForm(entry, { id: this.selectedEntryId });
    },
    executeActivity(activity, list, isFullView) {
      if (list.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }
      // to-do skipFormConfirmation
      // if (activity.skipConfirm && !activity.standard) {
      //   window.eventBus.$emit("formSkipConfirmation", {
      //     moduleId: this.moduleId && this.moduleId.toString(),
      //     activity: activity,
      //     activityId: activity.id,
      //     entryIds: list.map((x) => x.id)
      //   });
      // } else {
      window.eventBus.$emit('loadDetailViewForm', {
        entries: list,
        moduleId: this.moduleId,
        listingId: this.listingId,
        activityId: activity.id,
        fullView: isFullView
      });
      // }
    },
    executeAction(action, list) {
      commons.executeEntryCustom({
        action: action,
        items: list,
        modal: this.$refs.ftsActionPageModal
      });
    },
    async executeDocument(document, list) {
      if (list.length > BULK_MAX_COUNT) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      if (document.type == 'template') {
        this.$refs.documentModal.show({
          templateId: document.id,
          entryId: list[0].id
        });
      } else if (document.hasForm) {
        let items = this.listData.filter((x) => x.selected);
        await this.$refs.ftsDocumentFormModal.show({
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: document,
          items: items,
          filters: this.filters
        });
      } else {
        let parameters = {
          document: document,
          items: list,
          moduleId: this.moduleId,
          listingId: this.listingId,
          filters: this.filters,
        };

        if (document.dataSourceId == 'logicService') {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }

          let items = this.listData.filter((x) => x.selected);
          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
            parameters.inputs.locusIds = items.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    exportData() {
      const encodedUri = `/service/htmltemplateprocessing/document?documentmodel=${JSON.stringify(
        {
          parameters: {
            vectorId: this.moduleId,
            list: this.listingId,
            queryFilters: this.filters,
            context: clientStore.state.workspaceContext != null ?
              clientStore.state.workspaceContext.id :
              null
          }
        }
      )}`;
      window.open(`/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`, "_blank");
    },
    toggleControl() {
      this.enabledControl = !this.enabledControl;
      LocalStorageService.setModuleListingPreference(
        this.moduleId,
        this.listingId,
        {
          ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
          controlVisible: this.enabledControl
        }
      );
      if (this.enabledBulk) {
        this.enabledBulk = false;
        this.bulkSelectAll = false;
      }
    },
    openWidgetDesignPreviewModal() {
      this.$refs.ftsWidgetDesignPreviewModal.show({
        moduleId: this.moduleId,
        listingId: this.listingId,
        filters: this.filters,
        source: "listView"
      });
    },
    toggleBulk() {
      this.enabledBulk = !this.enabledBulk;
      if (!this.enabledBulk) {
        this.listData.forEach((x) => this.$delete(x, 'selected'));
      }
    },
    translateCommentContent(content) {
      if (!!content)
        return commons.translateCommentContent(content, this.users);
    },
    setFormSelectedActivity(activity) {
      this.formSelectedActivity = activity;
    },
    entryAction(item) {
      const action = this.appList.action;

      if(action ==='view') {
        this.loadForm(item, { id: this.selectedEntryId });
      }
      if (action === 'edit') {
        this.executeActivity({ id: 'edit' }, [item])
      }

      // Else: find from this.header.activities
      const matchedActivity = this.header.activities.find(
        activity => activity.id === action
      );

      if(!matchedActivity) return;

      this.executeActivity(matchedActivity, [item])

    },
    async saveDefaultFilters() {
      this.saveLoading = true;

      try {
        await canvasDesignStore.dispatch("loadVector", this.moduleId);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let menu = canvasDesign.getMenu(canvas, this.listingId);

        if (menu) {
          menu.defaultFilter = _.cloneDeep(this.filters);
        }

        canvasDesign.compactDesignBeforeSave(canvas);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        await canvasDesignStore.dispatch("saveVector");
      } catch(err) {
        this.$toasted.global.error(err, "Failed to save listing default filters.");
      }

      this.saveLoading = false;
    },
    async openListingFilters() {
      this.$refs.ftsListingFilterDesign.show({
        moduleId: this.moduleId,
        listing: {
          id: this.list.id,
          name: this.list.name,
          emoji: this.list.emoji
        }
      });
    }
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || clientStore.getters.workspaceBeta;
    },
    isCreateRight() {
      return this.isRightOf('create');
    },
    isQuickViewRight() {
      return this.isRightOf('quickView');
    },
    isViewRight() {
      return this.isRightOf('view');
    },
    isAssignRight() {
      return this.isRightOf('assign');
    },
    isCommentRight() {
      return this.isRightOf('comment');
    },
    isExportRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      return this.isRightOf('export');
    },
    isDashboardWidgetRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      return true;
    },
    list() {
      return clientStore.state.list;
    },
    filterColumns() {
      let filters = _.cloneDeep(this.columns);
      /* collaboration */
      filters.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === 'collaboration')
      });
      /* assign to */
      const assignToColIndex = filters.findIndex(x => x.id === 'assignTo') + 1;
      if (assignToColIndex)
        filters.splice(
          assignToColIndex,
          0,
          {
            ...queryLogic.querySelectionPrefix.find(x => x.id === 'due')
          }
        );
      /* created date */
      if (!filters.some(x => x.id == 'createdDate')) {
        const createdByColIndex = filters.findIndex(x => x.id === 'createdBy') + 1;
        if (createdByColIndex)
          filters.splice(
            createdByColIndex,
            0,
            {
              ...queryLogic.querySelectionPrefix.find(x => x.id === 'createdDate')
            }
          );
      }
      /* last change date */
      if (!filters.some(x => x.id == 'lastChangeDate')) {
        const lastChangeByColIndex = filters.findIndex(x => x.id === 'lastChangeBy') + 1;
        if (lastChangeByColIndex)
          filters.splice(
            lastChangeByColIndex,
            0,
            {
              ...queryLogic.querySelectionPrefix.find(x => x.id === 'lastChangeDate')
            }
          );
      }

      if (
        this.header && 
        this.header.defaultFilters && 
        Array.isArray(this.header.defaultFilters.items) && 
        this.header.defaultFilters.items.length
      ) {
        const flattenFilters = queryLogic.flattenFilters(this.filters);
        const nonExistingFilters = queryLogic.getNonExistingFilters(flattenFilters, filters);

        if (Array.isArray(nonExistingFilters) && nonExistingFilters.length) {
          filters = filters.concat(nonExistingFilters);
        }
      }
      /* query selection suffix */
      filters = filters.concat(
        queryLogic.querySelectionSuffix
          .filter(x => x.fieldName === 'and' || x.fieldName === 'or')
          .map(y => ({ ...y, system: true}))
      );

      return filters.filter((x) => {
        if (x.system) {
          return true;
        } else {
          const informationType = commons.informationTypes.find(
            (y) => y.id === x.type
          );
          return informationType && informationType.searchable;
        }
      });
    },
    headerStandards() {
      if (this.enabledBulk && this.listData.some((x) => x.selected)) {
        return _.intersectionWith(
          commons.standardActivities.filter((x) => x.bulk),
          this.header && this.header.standards,
          ({ id }, key) => id === key
        );
      }
      return [];
    },
    headerActivities() {
      if (this.header && this.header.activities && this.listData) {
        const selectedItems = this.listData.filter((x) => x.selected);
        if (selectedItems.length) {
          return this.header.activities.filter((x) => {
            if (x.disableBulkActions) {
              return false;
            }
            if (x.fromStates && x.fromStates.length) {
              return selectedItems.every((y) => x.fromStates.some((z) => z == y.state.id));
            }
            return true;
          });
        }
      }
      return [];
    },
    headerActions() {
      if (this.header && this.header.customs && this.listData) {
        let items = this.listData.filter((x) => x.selected);
        return this.header.customs.filter((x) => {
          if (Boolean(x.condition)) {
            try {
              return Boolean(eval(x.condition));
            } catch(err) {
              console.warn('Action:ConditionError', err);
              return false;
            }
          }
          return items.length ? x.isMultiple : x.isDefault;
        });
      }
      return [];
    },
    headerDocuments() {
      if (this.header && this.header.documents && this.listData) {
        const selectedItems = this.listData.filter((x) => x.selected);
        return this.header.documents.filter((x) =>
          selectedItems.length ?
            x.allowSelection :
            x.dataSourceId == 'collection' || x.dataSourceId == 'logicService'
        );
      }
      return [];
    },
    bulkSelectAll: {
      get() {
        return this.listData.length ? this.listData.every((x) => x.selected) : false;
      },
      set(val) {
        this.listData.forEach((x) => {
          this.$set(x, 'selected', val);
        });
      }
    },
    selectedEntryId() {
      if (this.$route.query.id) {
        const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
        return queryDecodeids && queryDecodeids.entryId;
      }
    },
    getTotalItemsLimit() {
      if (this.totalItemsLimit && commons.isInteger(this.totalItemsLimit) && this.totalItemsLimit > 0) {
        return this.totalItemsLimit;
      }

      return Listing_Max_Total_Entries;
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.getTotalItemsLimit;
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.debouncedApiCall();
      }
    }
  },
  created() {
    this.debouncedApiCall = _.debounce(() => {
      this.refreshList();
    }, 1000);

    this.refreshSchemaHandler = () => {
      this.refresh(true);
    }

    this.refreshListHandler = () => {
      this.refreshList();
    }

    this.formHandler = ({ moduleId, listingId, activityId }) => {
      if (!moduleId)
        return;

      const sameAsModule = this.moduleId == moduleId;

      if (activityId == "create") {
        if (sameAsModule) {
          this.executeActivity({ id: "create" }, []);
        } else {
          let query = { a: "create" };

          if (listingId) {
            query.l = listingId;
          }

          this.$router.push({
            name: "list-view",
            params: { moduleId: commons.encodeids(moduleId) },
            query: query
          });
        }
      }
    }

    window.eventBus.$on("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$on("listing:refresh", this.refreshListHandler);
    window.eventBus.$on("detailView:form", this.formHandler);
  },
  async mounted() {
    Vue.prototype.$ftsDetailView = true;

    try {
      await this.refresh(true);
      this.initial = true;

      if (!this.listError) {
        const localStorageValue = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
        this.enabledControl = Boolean(localStorageValue && localStorageValue.controlVisible);
        this.initialLoad = true;

        if (
          this.$route.query.id ||
          (this.isCreateRight && this.$route.query.a === 'create')
        ) {
          let entryId = null;
          if (this.$route.query.id) {
            const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
            if (queryDecodeids && queryDecodeids.entryId) {
              entryId = queryDecodeids.entryId;
            }
          }

          this.$nextTick(() => {
            this.loadDetailViewForm({
              id: entryId,
              moduleId: this.moduleId,
              listingId: this.listingId,
              activityId: this.$route.query.a,
              fullView: this.$route.query.m && this.$route.query.m.toLowerCase() === 'f'
            });
          });
        } else if (this.listData[0]) {
          this.$nextTick(() => {
            this.loadDetailViewForm({
              id: this.listData[0].id,
              title: this.listData[0].title,
              state: this.listData[0].state,
              audit: this.listData[0].audit,
              moduleId: this.moduleId,
              listingId: this.listingId,
              fullView: this.$route.query.m && this.$route.query.m.toLowerCase() === 'f'
            });
          });
        }
      }
    } catch(err) {
      console.error(err);
      this.listError = getListError(err);
      this.initial = true;
    }
  },
  beforeDestroy() {
    delete Vue.prototype.$ftsDetailView;
    window.eventBus.$off("listing:schemaUpdated", this.refreshSchemaHandler);
    window.eventBus.$off("listing:refresh", this.refreshListHandler);
    window.eventBus.$off("detailView:form", this.formHandler);

    if (this.debouncedApiCall && this.debouncedApiCall.cancel) {
      this.debouncedApiCall.cancel();
    }
  }
}
</script>

<style lang="scss" scoped>
@use "/scss/style.scss";
</style>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-list-view {
  &.list-view--detail-view {
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
  }
}
.fts-detail-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 0.25rem;
  .dv-header-action-container {
    @include flex-gap($gap: 0.5rem);
    margin-bottom: 1rem;
  }
  .dv-header-control-container {
    margin-bottom: 1rem;
  }
  .dv-header-control-wrapper {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .dv-header-control__r {
    flex: 1;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    margin-left: 0.5rem;
  }
  .dv-header-control-btn-wrapper {
  }
  .dv-header-control-action {
    font-size: 1rem;
    color: var(--fts-default);
    .tippy-trigger {
      cursor: pointer;
    }
    &:not(:first-child) {
      margin-left: 0.75rem;
    }
    &.dv-header-control-action--loading {
      cursor: progress;
    }
    &.dv-header-control-action--disabled {
      cursor: not-allowed;
    }
  }
  .dv-content {
    flex: 1;
    display: flex;
    border: 1px solid var(--fts-border);
    border-radius: $comfort-border-radius;
    overflow: hidden;
  }
  .dv-list {
    position: relative;
    flex: 1 0 325px;
    max-width: 325px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    overflow: hidden;
  }
  .dv-list__header {
    padding: 0.5rem;
    border-bottom: 1px solid var(--fts-border);
  }
  .dv-list-header-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    & + .dv-list-header-wrapper {
      margin-top: 0.5rem;
    }
    > div {
      display: flex;
      align-items: center;
      > *:not(:last-child) {
        margin-right: 0.75rem;
      }
    }
  }
  .dv-list-header-action {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-default);
    cursor: pointer;
    &.dv-list-header-action--active {
      color: var(--fts-text);
    }
    &.dv-list-header-action--disabled {
      cursor: not-allowed;
    }
  }
  .dv-list-header-btn.fts-btn {
    min-height: unset;
    padding: 0.25rem 0.5rem;
    font-weight: 400;
  }
  .dv-list-footer-action {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
    cursor: pointer;
  }
  .dv-list-pagination {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
    cursor: default;
  }
  .dv-list__content {
    flex: 1;
    overflow-x: hidden;
  }
  .dv-list__footer {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    border-top: 1px solid var(--fts-border);
    > *:not(:last-child) {
      margin-right: 0.625rem;
    }
  }
  .dv-list-entry {
    display: flex;
    align-items: flex-start;
    padding: 0.5rem;
    border-bottom: 1px solid var(--fts-border);
    cursor: pointer;
    &.dv-list-entry--active {
      background-color: rgba(var(--fts-primary-rgb), 0.085);
    }
  }
  .dv-list-entry__l {
    height: 1.25rem;
    display: flex;
    align-items: center;
    margin-right: 0.5rem;
  }
  .dv-list-entry__r {
    min-width: 0;
    flex: 1;
  }
  .dv-list-entry-main {
    min-width: 0;
    display: flex;
    align-items: flex-start;
  }
  .dv-list-entry-sub {
    display: flex;
    margin-top: 0.125rem;
    font-size: 0.875rem;
    color: var(--fts-default);
    .dv-list-entry-profile-date {
      margin-left: 0.5rem;
    }
    .dv-list-entry-assign-date {
      flex: 1;
      text-align: right;
    }
    > *:not(:last-child) {
      margin-right: 0.5rem;
    }
  }
  .dv-list-entry-title {
    @include text-line-clamp(
      $fontSize: 0.875rem,
      $lineHeight: 1.25rem,
      $lineClamp: 2
    );
    flex: 1;
    color: var(--fts-text);
    &:not(:last-child) {
      margin-right: 0.75rem;
    }
  }
  .dv-list-entry-assign {
    min-height: 1.25rem;
    display: flex;
    align-items: center;
    & + .dv-list-state-wrapper {
      margin-left: -0.25rem;
    }
  }
  .dv-list-entry-assign-date {
    cursor: default;
    &.dv-list-entry-assign-date--due {
      color: var(--fts-danger);
    }
  }
  .dv-list-state-wrapper {
    max-width: 25%;
  }
  .dv-list-state {
    display: block;
  }
  .dv-list-entry-profile {
    min-width: 0;
    display: flex;
    align-items: center;
  }
  .dv-list-entry-profile-name {
    font-size: 0.875rem;
    color: var(--fts-default);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dv-list-entry-profile-date {
    white-space: nowrap;
    cursor: default;
  }
  .dv-list-entry-comment {
    height: 1.25rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--fts-default);
    cursor: default;
    &:not(:last-child) {
      margin-right: 0.75rem;
    }
  }
  .dv-list-entry-comment-trigger {
    &.dv-list-entry-comment-trigger--active {
      color: var(--fts-primary);
    }
  }
  .dv-list-entry-comment-count {
    margin-left: 0.25rem;
    font-size: 0.75rem;
  }
  .dv-list-infinite-loading {
    padding: 1rem;
  }
  .dv-list-infinite-loading-message {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .dv-list-loading-overlay {
    @include flex-center-middle;
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: transparent;
    z-index: $z-index-loading-overlay;
    cursor: wait;
  }
  .dv-list-message {
    padding: 1.5rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    text-align:center;
    color: var(--fts-text);
  }
  .dv-list-message__title {
    margin-bottom: 0.15rem;
    font-weight: 500;
  }
  .dv-list-message__description {
    color: var(--fts-default);
  }
  .fts-detail-view-form {
    flex: 1;
  }
}
</style>
